import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useTranslation } from '@/components/i18n/Translator';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { findMessageElement, processMessageElementForRichText } from '@/utils/htmlProcessing';

interface Props {
  content: unknown;
  className?: string;
  messageId?: string;
}



const CopyButton = ({ content, className, messageId }: Props) => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const copyToClipboard = async () => {
    try {
      const plainTextContent =
        typeof content === 'object'
          ? JSON.stringify(content, null, 2)
          : String(content);

      // Try to copy rich text (HTML) if messageId is provided
      if (messageId && navigator.clipboard.write) {
        try {
          const messageElement = findMessageElement(messageId, 'CopyButton');

          if (messageElement) {
            const htmlContent = processMessageElementForRichText(messageElement, 'CopyButton');

            // Write both rich HTML and plain text to clipboard
            const clipboardItem = new ClipboardItem({
              'text/html': new Blob([htmlContent], { type: 'text/html' }),
              'text/plain': new Blob([plainTextContent], { type: 'text/plain' })
            });

            await navigator.clipboard.write([clipboardItem]);
            console.log('CopyButton: Successfully copied rich text to clipboard');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            return; // success
          } else {
            console.log('CopyButton: No messageElement found, falling back to plain text');
          }
        } catch (htmlError) {
          console.warn('Rich text copy failed, falling back to plain text:', htmlError);
        }
      }

      // Fallback: plain text only
      await navigator.clipboard.writeText(plainTextContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy: ' + String(err));
    }
  };

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={copyToClipboard}
            variant="ghost"
            size="icon"
            className={`text-muted-foreground ${className}`}
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {copied
              ? t('chat.messages.actions.copy.success')
              : t('chat.messages.actions.copy.button')}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CopyButton;
